import { useEffect, useState } from "react";
import { BarChart3, ShieldCheck, X } from "lucide-react";
import {
  getWebsiteConsent,
  saveWebsiteConsent,
  trackWebsitePageView,
  WEBSITE_CONSENT_EVENT,
  type WebsiteConsent,
} from "./analytics";

export function ConsentPanel() {
  const [open, setOpen] = useState(() => getWebsiteConsent() === null);
  const [customizing, setCustomizing] = useState(false);
  const [choice, setChoice] = useState<WebsiteConsent>(() => getWebsiteConsent() ?? { analytics: false, marketing: false });

  useEffect(() => {
    const openPreferences = () => {
      setChoice(getWebsiteConsent() ?? { analytics: false, marketing: false });
      setCustomizing(true);
      setOpen(true);
    };
    window.addEventListener("greenswap:open-privacy-choices", openPreferences);
    return () => window.removeEventListener("greenswap:open-privacy-choices", openPreferences);
  }, []);

  useEffect(() => {
    const sync = (event: Event) => setChoice((event as CustomEvent<WebsiteConsent>).detail);
    window.addEventListener(WEBSITE_CONSENT_EVENT, sync);
    return () => window.removeEventListener(WEBSITE_CONSENT_EVENT, sync);
  }, []);

  if (!open) return null;

  const save = (next: WebsiteConsent) => {
    const normalized = { analytics: next.analytics || next.marketing, marketing: next.marketing };
    saveWebsiteConsent(normalized);
    setChoice(normalized);
    setOpen(false);
    if (normalized.analytics) trackWebsitePageView();
  };

  return (
    <aside className={`consent-panel${customizing ? " is-customizing" : ""}`} aria-labelledby="consent-title" aria-live="polite">
      <div className="consent-panel-heading">
        <div><ShieldCheck aria-hidden="true" /><div><strong id="consent-title">Your privacy choices</strong><span>Essential website functions always stay on.</span></div></div>
        {getWebsiteConsent() && <button type="button" onClick={() => setOpen(false)} aria-label="Close privacy choices"><X /></button>}
      </div>
      <p>With your permission, GreenSwap uses analytics to learn which stories help people take a useful next step. We do not send inquiry text or item details.</p>
      {customizing ? <>
        <div className="consent-options">
          <label><input type="checkbox" checked={choice.analytics} onChange={(event) => setChoice((current) => ({ ...current, analytics: event.target.checked }))} /><BarChart3 aria-hidden="true" /><span><strong>Analytics</strong><small>Page journeys, experiments, and conversion steps</small></span></label>
          <label><input type="checkbox" checked={choice.marketing} onChange={(event) => setChoice((current) => ({ ...current, marketing: event.target.checked }))} /><span><strong>Marketing insights</strong><small>Aggregated acquisition and demographic reporting</small></span></label>
        </div>
        <div className="consent-actions">
          <button className="button button-quiet" type="button" onClick={() => save({ analytics: false, marketing: false })}>Essential only</button>
          <button className="button button-primary" type="button" onClick={() => save(choice)}>Save my choices</button>
        </div>
      </> : <div className="consent-actions consent-actions--compact">
        <button className="button button-quiet" type="button" onClick={() => save({ analytics: false, marketing: false })}>Essential only</button>
        <button className="button button-quiet" type="button" onClick={() => setCustomizing(true)}>Customize</button>
        <button className="button button-primary" type="button" onClick={() => save({ analytics: true, marketing: false })}>Allow analytics</button>
      </div>}
      <a href="/privacy">Read the website privacy notice</a>
    </aside>
  );
}
